import React, { useState, Fragment } from 'react';
import { Box, Typography, Grid, Switch, FormControlLabel, Button } from '@mui/material';
import { useStyles } from './styles';
import CheckIcon from '@mui/icons-material/Check';
import RemoveIcon from '@mui/icons-material/Remove';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next';
import NextLink from 'next/link';
import CustomButton from 'components/CustomButton';
import * as queryString from 'query-string';
import { useRouter } from 'next/router';
import { getMtgContinueUrl } from 'utils';
import PropTypes from 'prop-types';

const renderCellValue = (value) => {
  if (value === 'Yes') {
    return (
      <CheckIcon
        sx={{
          color: '#374266',
          width: '32px',
          height: '25px',
        }}
      />
    );
  }

  if (value === 'No') {
    return (
      <RemoveIcon
        sx={{
          color: '#374266',
          width: '32px',
          height: '22px',
        }}
      />
    );
  }

  return value;
};

const ProductFeaturesComparison = ({
  mtgPrimeData,
  researcherLifeData,
  switcher,
  researcherSwitcher,
  mtgPrimeDuration,
  userCountry,
  noDiscountPrice,
  yearlyPricePerMonth,
  monthlyPricePerMonth,
  researcherNoDiscountPrice,
  researcherYearlyPricePerMonth,
  researcherDuration,
  researcherButton,
  description,
  duration,
  button,
  isAuthenticated,
  sendButtonClickTrackingEvents,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('pricing');
  const router = useRouter();

  // For China, show only Free and Researcher; for others, show all 3 plans
  const showPrimePlan = userCountry !== 'CN';

  const handleClick = () => {
    if (typeof sendButtonClickTrackingEvents === 'function') {
      sendButtonClickTrackingEvents('Get started Starter Plan');
    }
  };
  const productData = [
    {
      logoSrc: '/images/pricing/mtg-feature.svg',
      title: t('product_features_comparison.categories.creating_illustrations'),
      features: [
        {
          name: t('product_features_comparison.features.expert_curated_illustrations'),
          free: t('product_features_comparison.values.limited_use'),
          researcher: t('product_features_comparison.values.unlimited'),
          prime: t('product_features_comparison.values.unlimited'),
        },
        {
          name: t('product_features_comparison.features.professionally_designed_templates'),
          free: t('product_features_comparison.values.limited_use'),
          researcher: t('product_features_comparison.values.yes'),
          prime: t('product_features_comparison.values.yes'),
        },
        {
          name: t('product_features_comparison.features.ai_image_generation'),
          free: t('product_features_comparison.values.limited_use'),
          researcher: t('product_features_comparison.values.unlimited'),
          prime: t('product_features_comparison.values.unlimited'),
        },
        {
          name: t('product_features_comparison.features.custom_expert_illustrations'),
          free: t('product_features_comparison.values.no'),
          researcher: t('product_features_comparison.values.yes'),
          prime: t('product_features_comparison.values.yes'),
        },
        {
          name: t('product_features_comparison.features.unlimited_vector_downloads'),
          free: t('product_features_comparison.values.no'),
          researcher: t('product_features_comparison.values.unlimited'),
          prime: t('product_features_comparison.values.unlimited'),
        },
        {
          name: t('product_features_comparison.features.full_publishing_rights'),
          free: t('product_features_comparison.values.no'),
          researcher: t('product_features_comparison.values.yes'),
          prime: t('product_features_comparison.values.yes'),
        },
        {
          name: t('product_features_comparison.features.unlimited_collaborators'),
          free: t('product_features_comparison.values.no'),
          researcher: t('product_features_comparison.values.yes'),
          prime: t('product_features_comparison.values.yes'),
        },
        {
          name: t('product_features_comparison.features.priority_customer_support'),
          free: t('product_features_comparison.values.no'),
          researcher: t('product_features_comparison.values.yes'),
          prime: t('product_features_comparison.values.yes'),
        },
      ],
    },
    {
      logoSrc: '/images/pricing/r-paperpal-logo.svg',
      title: t('product_features_comparison.categories.academic_writing'),
      features: [
        {
          name: t('product_features_comparison.features.language_suggestions'),
          free: t('product_features_comparison.values.200_per_month'),
          researcher: t('product_features_comparison.values.200_per_month'),
          prime: t('product_features_comparison.values.20k_edits_monthly'),
        },
        {
          name: t('product_features_comparison.features.write_polish'),
          free: t('product_features_comparison.values.5_uses_per_day'),
          researcher: t('product_features_comparison.values.5_uses_per_day'),
          prime: t('product_features_comparison.values.500_month'),
        },
        {
          name: t('product_features_comparison.features.research_writing_agents'),
          free: t('product_features_comparison.values.limited_use'),
          researcher: t('product_features_comparison.values.limited_use'),
          prime: t('product_features_comparison.values.10x'),
        },
        {
          name: t('product_features_comparison.features.plagiarism_check'),
          free: t('product_features_comparison.values.lite_7k'),
          researcher: t('product_features_comparison.values.lite_7k'),
          prime: t('product_features_comparison.values.10k_words_mo'),
        },
        {
          name: t('product_features_comparison.features.reference_check'),
          free: t('product_features_comparison.values.no'),
          researcher: t('product_features_comparison.values.no'),
          prime: t('product_features_comparison.values.10k_words_mo'),
        },
        {
          name: t('product_features_comparison.features.ai_detection'),
          free: t('product_features_comparison.values.preview'),
          researcher: t('product_features_comparison.values.preview'),
          prime: t('product_features_comparison.values.25k_words_mo'),
        },
      ],
    },
    {
      logoSrc: '/images/pricing/r-discovery.svg',
      title: t('product_features_comparison.categories.literature_search'),
      features: [
        {
          name: t('product_features_comparison.features.ai_assistant_chat_pdf'),
          free: t('product_features_comparison.values.limited_use'),
          researcher: t('product_features_comparison.values.limited_use'),
          prime: t('product_features_comparison.values.unlimited'),
        },
        {
          name: t('product_features_comparison.features.literature_maps'),
          free: t('product_features_comparison.values.no'),
          researcher: t('product_features_comparison.values.no'),
          prime: t('product_features_comparison.values.yes'),
        },
        {
          name: t('product_features_comparison.features.audio_papers_translation'),
          free: t('product_features_comparison.values.limited_use'),
          researcher: t('product_features_comparison.values.limited_use'),
          prime: t('product_features_comparison.values.unlimited'),
        },
        {
          name: t('product_features_comparison.features.content_feeds'),
          free: t('product_features_comparison.values.no'),
          researcher: t('product_features_comparison.values.no'),
          prime: t('product_features_comparison.values.yes'),
        },
      ],
    },
    {
      logoSrc: '',
      title: t('product_features_comparison.categories.other'),
      features: [
        {
          name: t('product_features_comparison.features.research_skill_courses'),
          free: t('product_features_comparison.values.no'),
          researcher: t('product_features_comparison.values.no'),
          prime: t('product_features_comparison.values.yes'),
        },
        {
          name: t('product_features_comparison.features.journal_finder'),
          free: t('product_features_comparison.values.no'),
          researcher: t('product_features_comparison.values.no'),
          prime: t('product_features_comparison.values.yes'),
        },
      ],
    },
  ];
  function getTrialPlanRedirectUrl() {
    if (isAuthenticated) {
      return `/my-account/overview`;
    } else {
      return `/auth/login-register?${queryString.stringify({
        from_url: router.basePath + router.pathname,
        redirect: getMtgContinueUrl(),
      })}`;
    }
  }

  return (
    <Box className={classes.outerWrapper}>
      <Box className={classes.featureComparison}>
        {/* Section Heading */}
        <Typography variant="h2" className={classes.sectionHeading}>
          {t('product_features_comparison.section_title')}
        </Typography>

        {/* Plans Section */}
        <Box className={classes.plansSticky}>
          <Box className={clsx(classes.plansGrid, !showPrimePlan && classes.plansGridTwoCol)}>
            <Box className={classes.empty}>{t('product_features_comparison.plans')}</Box>

            {/* Free Plan Card */}
            <Box className={`${classes.comparePlanCard} ${classes.free}`}>
              <Typography variant="h3" style={{ marginTop: '6px' }} className={classes.cardHeading}>
                {t('product_features_comparison.free_plan')}
              </Typography>
              <Box className={`${classes.comparePrice} ${classes.freePricing}`}>
                <Box className={classes.bottomContainer}>
                  <Typography component="span" className={classes.freePrice}>
                    {mtgPrimeData['annual'].exchange_currency_symbol}0{' '}
                  </Typography>
                  <span className={classes.subDescription}>
                    {t('product_features_comparison.no_credit_card_required')}
                  </span>
                </Box>
              </Box>

              <NextLink passHref={true} href={getTrialPlanRedirectUrl()}>
                <CustomButton
                  variant="customGrayOutline"
                  style={{
                    width: '100%',
                    fontFamily: 'Lato',
                    fontWeight: 600,
                    fontStyle: 'SemiBold',
                    fontSize: '16px',
                    lineHeight: '24px',
                    letterSpacing: '0%',
                    textAlign: 'center',
                  }}
                  onClick={handleClick}
                >
                  {t('Get Started')}
                </CustomButton>
              </NextLink>
            </Box>

            {/* Researcher Plan Card */}
            <Box className={`${classes.comparePlanCard} ${classes.researcher}`}>
              <Typography variant="h3" className={classes.cardHeading}>
                {t('product_features_comparison.researcher_plan')}
                <span className={classes.primeImage}>
                  <img src="/images/pricing/prime-2.svg" alt="Prime Plan"></img>
                </span>
              </Typography>
              <Box>
                {researcherSwitcher && (
                  <Box className={classes.switchBox}>
                    {researcherSwitcher.options.map((option) => (
                      <Button
                        key={option.id}
                        onClick={() => researcherSwitcher.setDuration(option.id)}
                        className={clsx(
                          classes.switch,
                          researcherSwitcher.duration === option.id && [
                            classes.activeSwitch,
                            classes.activeSwitchBlue,
                          ],
                        )}
                      >
                        {option.name}
                      </Button>
                    ))}
                  </Box>
                )}
                <Typography
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: '4px',
                    marginBottom: '8px',
                  }}
                >
                  <Typography component="span" className={classes.mainPriceValue}>
                    {researcherYearlyPricePerMonth}
                  </Typography>
                </Typography>
                <Typography component="span" className={classes.mainPriceMonth}>
                  {researcherNoDiscountPrice && (
                    <Typography component="span" className={classes.noDiscountPriceNew}>
                      {researcherNoDiscountPrice}
                    </Typography>
                  )}
                  {t('per_month')} {researcherDuration === 'annual' && `, ${t('billed yearly')}`}
                  {researcherDuration === 'monthly' && `, ${t('billed monthly')}`}
                </Typography>
              </Box>
              <Box className={classes.ctaContainer}>{researcherButton}</Box>
            </Box>

            {/* Prime Plan Card - Hidden for China */}
            {showPrimePlan && (
              <Box className={`${classes.comparePlanCard} ${classes.prime}`}>
                <Typography variant="h3" className={classes.cardHeading}>
                  {t('product_features_comparison.prime_plan')}
                  <span className={classes.primeImage}>
                    <img src="/images/pricing/prime-2.svg" alt="Prime Plan"></img>
                  </span>
                </Typography>
                <Box>
                  {switcher && (
                    <Box className={classes.switchBox}>
                      {switcher.options.map((option) => (
                        <Button
                          key={option.id}
                          onClick={() => switcher.setDuration(option.id)}
                          className={clsx(
                            classes.switch,
                            switcher.duration === option.id && [
                              classes.activeSwitch,
                              classes.activeSwitchBlue,
                            ],
                          )}
                        >
                          {option.name}
                        </Button>
                      ))}
                    </Box>
                  )}
                  <Typography
                    style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: '4px',
                      marginBottom: '8px',
                    }}
                  >
                    <Typography component="span" className={classes.mainPriceValue}>
                      {yearlyPricePerMonth}
                    </Typography>
                  </Typography>
                  <Typography component="span" className={classes.mainPriceMonth}>
                    {noDiscountPrice && (
                      <Typography component="span" className={classes.noDiscountPriceNew}>
                        {noDiscountPrice}
                      </Typography>
                    )}
                    {t('per_month')} {duration === 'annual' && `, ${t('billed yearly')}`}
                    {duration === 'monthly' && `, ${t('billed monthly')}`}
                    {duration === 'quarterly' && `, ${t('billed quarterly')}`}
                  </Typography>
                </Box>
                <Box className={classes.ctaContainer}>{button}</Box>
              </Box>
            )}
          </Box>
        </Box>

        {/* Features List Section - Looping Over Features */}
        {productData.map((product, index) => (
          <Box key={index} className={classes.productContainer}>
            {/* Product Logo and Title */}
            <Box className={classes.productName}>
              {product.logoSrc && product.logoSrc !== '' && (
                <img
                  src={product.logoSrc}
                  alt={product.title}
                  loading="lazy"
                  width="110"
                  height="30"
                />
              )}
              <span>{product.title}</span>
            </Box>

            {/* Features List for Each Product */}
            <Box className={classes.features}>
              {product.features.map((feature, featureIndex) => (
                <Box
                  key={featureIndex}
                  className={clsx(classes.featureRow, !showPrimePlan && classes.featureRowTwoCol)}
                >
                  <Box className={classes.featureName}>{feature.name}</Box>
                  <Box className={classes.featureValue}>{renderCellValue(feature.free)}</Box>
                  <Box className={`${classes.featureValue} ${classes.researcher}`}>
                    {renderCellValue(feature.researcher)}
                  </Box>
                  {showPrimePlan && (
                    <Box className={`${classes.featureValue} ${classes.prime}`}>
                      {renderCellValue(feature.prime)}
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

ProductFeaturesComparison.propTypes = {
  mtgPrimeData: PropTypes.object,
  researcherLifeData: PropTypes.object,
  switcher: PropTypes.object,
  researcherSwitcher: PropTypes.object,
  mtgPrimeDuration: PropTypes.string,
  userCountry: PropTypes.string,
  noDiscountPrice: PropTypes.string,
  yearlyPricePerMonth: PropTypes.string,
  monthlyPricePerMonth: PropTypes.string,
  researcherNoDiscountPrice: PropTypes.string,
  researcherYearlyPricePerMonth: PropTypes.string,
  researcherDuration: PropTypes.string,
  researcherButton: PropTypes.element,
  description: PropTypes.string,
  duration: PropTypes.string,
  button: PropTypes.element,
  isAuthenticated: PropTypes.bool,
  sendButtonClickTrackingEvents: PropTypes.func,
};

export default ProductFeaturesComparison;
